/**
 * /pdf-templates/:clientId/:docType — Editor de plantilla PDF.
 *
 * - Monaco editor (HTML) con el template actual (custom o default).
 * - Botones: Guardar / Generar preview / Restaurar default / Volver.
 * - PDF preview en iframe (lado derecho), generado on-demand contra
 *   POST /admin/.../preview con HTML del editor (no requiere guardar).
 *
 * El editor cargar el HTML inicial desde el backend (default si no
 * hay custom). El usuario edita; al guardar se hace PUT (incrementa
 * version). Restaurar default hace DELETE (vuelve al embedded).
 */

import { useEffect, useState } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { ArrowLeft, Eye, RotateCcw, Save } from 'lucide-react'
import { toast } from 'sonner'

import { MonacoHtmlEditor } from './components/monaco-html-editor'
import { PdfPreview } from './components/pdf-preview'
import {
  useDeletePdfTemplate,
  usePdfTemplate,
  useUpsertPdfTemplate,
} from '@/hooks/api/use-pdf-templates'
import {
  DOC_TYPES,
  DOC_TYPE_LABELS,
  pdfTemplatesApi,
  type DocType,
} from '@/lib/api-pdf-templates'

export function PdfTemplateEditorPage() {
  const { clientId, docType } = useParams<{ clientId: string; docType: string }>()

  // Validación de docType (URL → enum).
  const isValidDocType = !!docType && (DOC_TYPES as readonly string[]).includes(docType)
  if (!clientId || !isValidDocType) {
    return <Navigate to="/pdf-templates" replace />
  }
  const dt = docType as DocType

  const tplQ = usePdfTemplate(clientId, dt)
  const upsert = useUpsertPdfTemplate()
  const del = useDeletePdfTemplate()

  const [html, setHtml] = useState<string>('')
  const [previewBlob, setPreviewBlob] = useState<Blob | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewError, setPreviewError] = useState<string | null>(null)

  // Cargar HTML inicial desde el backend cuando el query resuelve.
  useEffect(() => {
    if (tplQ.data && html === '') {
      setHtml(tplQ.data.html)
    }
  }, [tplQ.data, html])

  const isCustom = tplQ.data?.source === 'db'
  const canSave = html.length > 0 && html !== tplQ.data?.html
  const dirty = canSave

  const handleGeneratePreview = async () => {
    setPreviewLoading(true)
    setPreviewError(null)
    try {
      const blob = await pdfTemplatesApi.preview(clientId, dt, html)
      setPreviewBlob(blob)
    } catch (e: unknown) {
      const err = e as { response?: { status?: number; data?: unknown }; message?: string }
      if (err?.response?.status === 503) {
        // Backend: renderer_unavailable — el renderer PDF no está
        // configurado en este deploy (pdf_template_handler.go).
        setPreviewError(
          'La generación de previews no está disponible en este servidor (renderer PDF deshabilitado). El guardado de la plantilla sí funciona.',
        )
      } else {
        // El preview usa responseType: 'blob' — si el backend devolvió un
        // error JSON, viene como Blob y hay que leerlo para extraer message.
        const msg = (await messageFromBlobError(err?.response?.data)) || err?.message || 'error desconocido'
        setPreviewError(`No se pudo renderizar el preview: ${msg}`)
      }
    } finally {
      setPreviewLoading(false)
    }
  }

  const handleSave = () => {
    upsert.mutate(
      { clientId, docType: dt, html },
      { onSuccess: () => toast.success(`Versión guardada (v${(tplQ.data?.version ?? 0) + 1})`) },
    )
  }

  const handleRestore = () => {
    if (!confirm(`¿Restaurar el default Taxor para "${DOC_TYPE_LABELS[dt]}"? Se perderá la plantilla custom.`)) {
      return
    }
    del.mutate({ clientId, docType: dt })
  }

  return (
    <div className="mx-auto max-w-7xl">
      {/* Header */}
      <div className="mb-6 flex items-end justify-between gap-4">
        <div>
          <Link
            to="/pdf-templates"
            className="mb-2 inline-flex items-center gap-1.5 text-xs text-fg-muted hover:text-fg-primary"
          >
            <ArrowLeft className="h-3 w-3" />
            Volver a plantillas
          </Link>
          <h1 className="text-2xl font-bold text-fg-primary">
            {DOC_TYPE_LABELS[dt]}
            {isCustom ? (
              <span className="ml-3 align-middle text-xs font-medium uppercase tracking-wider text-brand-700">
                custom v{tplQ.data?.version}
              </span>
            ) : (
              <span className="ml-3 align-middle text-xs font-medium uppercase tracking-wider text-fg-muted">
                default Taxor
              </span>
            )}
          </h1>
          <p className="mt-1 text-xs text-fg-muted">
            Tax-Client: <code className="font-mono">{clientId}</code>
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleGeneratePreview}
            disabled={previewLoading || !html}
            className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm font-medium text-fg-secondary hover:bg-bg-elevated hover:text-fg-primary disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Eye className="h-4 w-4" />
            Generar preview
          </button>
          {isCustom && (
            <button
              type="button"
              onClick={handleRestore}
              disabled={del.isPending}
              className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm font-medium text-fg-secondary hover:bg-bg-elevated hover:text-fg-primary disabled:cursor-not-allowed disabled:opacity-60"
            >
              <RotateCcw className="h-4 w-4" />
              Restaurar default
            </button>
          )}
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave || upsert.isPending}
            className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Save className="h-4 w-4" />
            {upsert.isPending ? 'Guardando…' : dirty ? 'Guardar cambios' : 'Sin cambios'}
          </button>
        </div>
      </div>

      {tplQ.isLoading && (
        <p className="text-center text-sm text-fg-muted">Cargando plantilla…</p>
      )}
      {tplQ.isError && (
        <p className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          No se pudo cargar la plantilla: {queryErrorMessage(tplQ.error)}
        </p>
      )}

      {/* Editor + Preview side by side */}
      {tplQ.data && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div>
            <p className="mb-1 text-xs font-medium uppercase tracking-wider text-fg-muted">
              HTML del template
            </p>
            <MonacoHtmlEditor value={html} onChange={setHtml} />
          </div>
          <div>
            <p className="mb-1 text-xs font-medium uppercase tracking-wider text-fg-muted">
              Preview PDF (datos sample)
            </p>
            <PdfPreview blob={previewBlob} loading={previewLoading} error={previewError} />
          </div>
        </div>
      )}

      {/* Help */}
      <div className="mt-6 rounded-md border border-border bg-bg-surface p-4 text-xs text-fg-secondary">
        <p className="mb-1 font-semibold text-fg-primary">Variables disponibles en el template:</p>
        <code className="block whitespace-pre-wrap font-mono text-[11px] leading-relaxed text-fg-muted">
          .DocType .DocNumber .IssueDate .IssueTime .CUFE .QRCodeURL{'\n'}
          .ResolutionNumber .ResolutionPrefix .ResolutionFrom .ResolutionTo{'\n'}
          .Issuer.NIT .Issuer.LegalName .Issuer.Address .Issuer.City ...{'\n'}
          .Buyer.NIT .Buyer.LegalName .Buyer.Address ...{'\n'}
          .Lines (range): .LineNumber .Description .Quantity .UnitPrice .TaxIVAAmount .LineTotal{'\n'}
          .Subtotal .TaxIVA .TaxICA .TaxINC .GrandTotal .Currency{'\n'}
          .ReferencedFENumber .ReferencedFECUFE .DiscrepancyReason (NC/ND){'\n\n'}
          Helpers: formatCOP, formatDate, formatDateTime, qrDataURI, upper, join
        </code>
      </div>
    </div>
  )
}

/** Mensaje legible (español si el backend lo da) desde un error de query. */
function queryErrorMessage(err: unknown): string {
  const e = err as { response?: { data?: { message?: string } }; message?: string }
  return e?.response?.data?.message || e?.message || 'error desconocido'
}

/**
 * Extrae `message` de un error del backend recibido como Blob (los
 * requests con responseType 'blob' serializan también los errores JSON
 * como Blob). Retorna null si no se puede parsear.
 */
async function messageFromBlobError(data: unknown): Promise<string | null> {
  if (!(data instanceof Blob)) {
    const d = data as { message?: string } | undefined
    return typeof d?.message === 'string' ? d.message : null
  }
  try {
    const parsed = JSON.parse(await data.text()) as { message?: string }
    return typeof parsed?.message === 'string' ? parsed.message : null
  } catch {
    return null
  }
}
